import type { ComponentType } from "react";
import {
  Calendar,
  MapPin,
  BarChart3,
  Car,
  Cast,
  Clock,
  Inbox,
  Radio,
  Users,
  UsersRound,
} from "lucide-react";

import HomeRoute from "@/pages/HomeRoute";
import { EventsPage } from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import EventCreatePage from "@/pages/EventCreatePage";
import EventEditPage from "@/pages/EventEditPage";
import VehicleModelsPage from "@/pages/VehicleModelsPage";
import TracksPage from "@/pages/TracksPage";
import TrackLeaderboardPage from "@/pages/TrackLeaderboardPage";
import TrackDetailPage from "@/pages/TrackDetailPage";
import TeamsPage from "@/pages/TeamsPage";
import TeamPage from "@/pages/TeamPage";
import DashboardPage from "@/pages/DashboardPage";
import VehiclesPage from "@/pages/VehiclesPage";
import SessionsPage from "@/pages/SessionsPage";
import InboxPage from "@/pages/InboxPage";
import LiveStreamsPage from "@/pages/LiveStreamsPage";
import SessionDetailPage from "@/pages/SessionDetailPage";
import SessionAnalyticsPage from "@/pages/SessionAnalyticsPage";
import LapComparisonPage from "@/pages/LapComparisonPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import DriversPage from "@/pages/DriversPage";
import MyPage from "@/pages/MyPage";
import FootageUsagePage from "@/pages/FootageUsagePage";
import LinkDevicePage from "@/pages/LinkDevicePage";
import TrackcastPage from "@/pages/TrackcastPage";
import TrackcastAndroidPage from "@/pages/TrackcastAndroidPage";
import TeamLivePage from "@/pages/TeamLivePage";
import PricingPage from "@/pages/PricingPage";
import WatchPage from "@/pages/WatchPage";
import AdminRecordingWatchPage from "@/pages/AdminRecordingWatchPage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ChangePasswordPage from "@/pages/auth/ChangePasswordPage";
import AcceptInvitationPage from "@/pages/auth/AcceptInvitationPage";
import type { PageMetaInput } from "@/hooks/usePageMeta";

// A single source of truth for the application routes.  The `public` flag
// indicates that the path can be accessed without authentication.
// The `standalone` flag indicates the route should be rendered outside the Layout.
export interface AppRoute {
  path: string;
  component: ComponentType;
  label?: string; // label for sidebar display (omit for non-sidebar routes)
  icon?: ComponentType<{ className?: string }>;
  public: boolean;
  standalone?: boolean; // render outside Layout component
  group?: string; // sidebar group label key; groupless routes form the main group
  badge?: string; // small badge label key rendered after the sidebar label
  meta?: PageMetaInput; // static document title/description applied on navigation
}

export const appRoutes: AppRoute[] = [
  {
    path: "/login",
    component: LoginPage,
    public: true,
    standalone: true,
    meta: { titleKey: "pages:titles.login" },
  },
  {
    path: "/register",
    component: RegisterPage,
    public: true,
    standalone: true,
    meta: { titleKey: "pages:titles.register" },
  },
  {
    path: "/change-password",
    component: ChangePasswordPage,
    public: true,
    standalone: true,
    meta: { titleKey: "pages:titles.changePassword" },
  },
  {
    path: "/invitations/accept",
    component: AcceptInvitationPage,
    public: true,
    standalone: true,
    meta: { titleKey: "pages:titles.acceptInvitation" },
  },
  {
    path: "/link-device",
    component: LinkDevicePage,
    public: false,
    standalone: true,
    meta: { titleKey: "pages:titles.linkDevice" },
  },
  {
    path: "/",
    component: HomeRoute,
    public: true,
    standalone: true,
    // No sidebar label/icon: a "Home" nav link would just redirect
    // authenticated users to /dashboard, redundant with the existing
    // /dashboard entry.
  },
  {
    path: "/dashboard",
    component: DashboardPage,
    label: "navigation.dashboard",
    icon: BarChart3,
    public: false,
    meta: { titleKey: "pages:titles.dashboard" },
  },
  {
    path: "/events",
    component: EventsPage,
    label: "navigation.events",
    icon: Calendar,
    public: true,
    meta: { titleKey: "pages:titles.events" },
  },
  {
    path: "/events/new",
    component: EventCreatePage,
    public: false,
    meta: { titleKey: "pages:titles.eventCreate" },
  },
  {
    path: "/events/:eventId",
    component: EventDetailPage,
    public: true,
    meta: { titleKey: "pages:titles.eventDetail" },
  },
  {
    path: "/events/:eventId/edit",
    component: EventEditPage,
    public: false,
    meta: { titleKey: "pages:titles.eventEdit" },
  },
  {
    path: "/sessions",
    component: SessionsPage,
    label: "navigation.sessions",
    icon: Clock,
    public: false,
    meta: { titleKey: "pages:titles.sessions" },
  },
  {
    path: "/sessions/my",
    component: SessionsPage,
    public: false,
    meta: { titleKey: "pages:titles.mySessions" },
  },
  {
    path: "/sessions/all",
    component: SessionsPage,
    public: true,
    meta: { titleKey: "pages:titles.allSessions" },
  },
  {
    path: "/vehicle-models",
    component: VehicleModelsPage,
    public: true,
    meta: { titleKey: "pages:titles.vehicleModels" },
  },
  {
    path: "/vehicles",
    component: VehiclesPage,
    label: "navigation.vehicles",
    icon: Car,
    public: false,
    meta: { titleKey: "pages:titles.vehicles" },
  },
  {
    path: "/tracks",
    component: TracksPage,
    label: "navigation.tracks",
    icon: MapPin,
    public: true,
    meta: { titleKey: "pages:titles.tracks" },
  },
  {
    path: "/tracks/:trackId/leaderboard",
    component: TrackLeaderboardPage,
    public: true,
    meta: { titleKey: "pages:titles.leaderboard" },
  },
  {
    path: "/tracks/:trackId",
    component: TrackDetailPage,
    public: true,
    meta: { titleKey: "pages:titles.trackDetail" },
  },
  {
    path: "/teams",
    component: TeamsPage,
    label: "navigation.teams",
    icon: UsersRound,
    public: true,
    meta: { titleKey: "pages:titles.teams" },
  },
  {
    path: "/teams/:teamId",
    component: TeamPage,
    public: true,
    meta: { titleKey: "pages:titles.teamDetail" },
  },
  {
    path: "/drivers",
    component: DriversPage,
    label: "navigation.drivers",
    icon: Users,
    public: true,
    meta: { titleKey: "pages:titles.drivers" },
  },
  // Hidden page: reachable at /plans by direct URL only. Intentionally absent
  // from the sidebar, the landing nav/footer (components/home/constants.ts),
  // the usage card CTA and the prerender list until quota enforcement /
  // billing actually ship.
  {
    path: "/plans",
    component: PricingPage,
    public: true,
    meta: { titleKey: "pages:titles.plans" },
  },
  // Trackcast section: capture-first surfaces (Inbox), the personal live
  // wall (Live Streams) + the getting-started page. Grouped at the bottom
  // of the sidebar; keep these entries contiguous.
  {
    path: "/inbox",
    component: InboxPage,
    label: "navigation.inbox",
    icon: Inbox,
    public: false,
    group: "navigation.groupTrackcast",
    meta: { titleKey: "pages:titles.inbox" },
  },
  {
    path: "/live-streams",
    component: LiveStreamsPage,
    label: "navigation.liveStreams",
    icon: Radio,
    public: false,
    group: "navigation.groupTrackcast",
    meta: { titleKey: "pages:titles.liveStreams" },
  },
  {
    path: "/trackcast",
    component: TrackcastPage,
    label: "navigation.trackcast",
    icon: Cast,
    public: true,
    group: "navigation.groupTrackcast",
    badge: "navigation.badgeNew",
    meta: { titleKey: "pages:titles.trackcast" },
  },
  {
    path: "/trackcast/android",
    component: TrackcastAndroidPage,
    public: true,
    meta: { titleKey: "pages:titles.trackcastAndroid" },
  },
  {
    path: "/sessions/:sessionId",
    component: SessionDetailPage,
    public: false,
    meta: { titleKey: "pages:titles.sessionDetail" },
  },
  {
    path: "/sessions/:sessionId/analytics",
    component: SessionAnalyticsPage,
    public: false,
    standalone: true,
    meta: { titleKey: "pages:titles.sessionAnalytics" },
  },
  {
    path: "/laps/compare",
    component: LapComparisonPage,
    public: false,
    standalone: true,
    meta: { titleKey: "pages:titles.lapComparison" },
  },
  {
    path: "/vehicles/:vehicleId",
    component: VehicleDetailPage,
    public: false,
    meta: { titleKey: "pages:titles.vehicleDetail" },
  },
  {
    path: "/my-page",
    component: MyPage,
    public: false,
    meta: { titleKey: "pages:titles.myPage" },
  },
  {
    path: "/footage-usage",
    component: FootageUsagePage,
    public: false,
    meta: { titleKey: "pages:titles.footageUsage" },
  },
  {
    path: "/watch/:sessionId",
    component: WatchPage,
    public: true,
    standalone: true,
    meta: { titleKey: "pages:titles.watch" },
  },
  // Admin-only single-recording viewer: standalone dark ground like /watch,
  // but gated by RequireAuth (backend enforces the ADMIN_EMAILS allowlist).
  {
    path: "/admin/watch/:recordingId",
    component: AdminRecordingWatchPage,
    public: false,
    standalone: true,
    meta: { titleKey: "pages:titles.watch" },
  },
  // Team multiview (issue #446): full-screen dark console, so standalone
  // like /watch, but restricted to authenticated team members.
  {
    path: "/teams/:teamId/live",
    component: TeamLivePage,
    public: false,
    standalone: true,
    meta: { titleKey: "pages:titles.teamLive" },
  },
];
